import { useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { clearCredentials } from '../../store/slices/authSlice';
import { useFeatureFlags } from '../../hooks/useFeatureFlags';
import { useInactivityTimer } from '../../hooks/useInactivityTimer';
import { usePageViewLogger } from '../../hooks/usePageViewLogger';
import {
  AppBar,
  Box,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Avatar,
  Menu,
  MenuItem,
  Divider,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard,
  People,
  EventNote,
  EmojiEvents,
  School,
  FitnessCenter,
  AdminPanelSettings,
  AccountCircle,
  CalendarMonth,
  Newspaper,
  Description,
  NotificationsActive,
} from '@mui/icons-material';
import NotificationCenter from './NotificationCenter';

const drawerWidth = 240;

export const MainLayout = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const { features } = useFeatureFlags();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  // Initialize inactivity timer
  useInactivityTimer();

  // Log page/tab views for the activity log
  usePageViewLogger();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  // Navigate to a tab and close the mobile drawer so it doesn't stay open
  // over the content on small screens.
  const handleMenuItemClick = (path: string) => {
    navigate(path);
    setMobileOpen(false);
  };

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    dispatch(clearCredentials());
    navigate('/');
  };

  const isStaff = user?.role === 'ADMIN' || user?.role === 'HEAD_COACH' || user?.role === 'COACH';

  const menuItems = [
    { text: 'Dashboard', icon: <Dashboard />, path: '/dashboard' },
    { text: 'Calendar', icon: <CalendarMonth />, path: '/calendar' },
    { text: 'GKM Squads', icon: <People />, path: '/squads' },
    { text: 'My squads', icon: <EventNote />, path: '/sessions' },
    { text: isStaff ? 'Competitions' : 'My competitions', icon: <EmojiEvents />, path: '/competitions', enabled: features.competitions },
    { text: 'Knowledge Center', icon: <School />, path: '/knowledge', enabled: features.knowledge },
    { text: 'S&C', icon: <FitnessCenter />, path: '/snc', enabled: features.snc },
    { text: 'News', icon: <Newspaper />, path: '/news', enabled: features.news },
  ];

  // Add session plans for coaches and admins only (after My competitions)
  if (user?.role === 'ADMIN' || user?.role === 'HEAD_COACH' || user?.role === 'COACH') {
    menuItems.splice(5, 0, {
      text: 'Session Plans',
      icon: <Description />,
      path: '/session-plans',
    });
  }

  // Add admin/head coach/coach menu items
  if (user?.role === 'ADMIN' || user?.role === 'HEAD_COACH' || user?.role === 'COACH') {
    menuItems.push({
      text: 'Notification Center',
      icon: <NotificationsActive />,
      path: '/notifications/center',
    });
  }

  if (user?.role === 'ADMIN' || user?.role === 'HEAD_COACH') {
    menuItems.push({
      text: 'User Management',
      icon: <People />,
      path: '/users',
    });
  }

  if (user?.role === 'ADMIN') {
    menuItems.push({
      text: 'Administration',
      icon: <AdminPanelSettings />,
      path: '/admin',
    });
  }

  // Filter out disabled features
  const visibleMenuItems = menuItems.filter(item => item.enabled !== false);

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Toolbar
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          py: 2,
          bgcolor: 'primary.main',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <Box
            component="img"
            src="/logo.bmp"
            alt="GKM Tennis Academy"
            sx={{
              width: 60,
              height: 'auto',
              borderRadius: '8px',
            }}
          />
          <Typography variant="subtitle2" fontWeight={700} color="white" sx={{ textAlign: 'center' }}>
            GKM Tennis
          </Typography>
        </Box>
      </Toolbar>
      <Divider />
      <List sx={{ flexGrow: 1, py: 2 }}>
        {visibleMenuItems.map((item) => (
          <ListItem key={item.text} disablePadding sx={{ mb: 0.5, px: 1 }}>
            <ListItemButton
              onClick={() => handleMenuItemClick(item.path)}
              sx={{
                borderRadius: 2,
                '&:hover': {
                  bgcolor: 'primary.light',
                  '& .MuiListItemIcon-root': {
                    color: 'white',
                  },
                  '& .MuiListItemText-primary': {
                    color: 'white',
                    fontWeight: 600,
                  },
                },
              }}
            >
              <ListItemIcon sx={{ color: 'primary.main', minWidth: 40 }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText
                primary={item.text}
                primaryTypographyProps={{
                  fontSize: '0.9rem',
                  fontWeight: 500,
                }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
          bgcolor: 'white',
          color: 'text.primary',
        }}
        elevation={1}
      >
        <Toolbar>
          <IconButton
            color="primary"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1, fontWeight: 600, color: 'primary.main' }}>
            GKM Tennis Academy
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <NotificationCenter />
            <Box sx={{ textAlign: 'right', display: { xs: 'none', sm: 'block' } }}>
              <Typography variant="body2" fontWeight={600}>
                {user?.firstName} {user?.lastName}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {user?.role?.replace('_', ' ')}
              </Typography>
            </Box>
            <IconButton onClick={handleProfileMenuOpen} sx={{ p: 0.5 }}>
              <Avatar sx={{ width: 40, height: 40, bgcolor: 'primary.main', fontWeight: 600 }}>
                {user?.firstName?.charAt(0)}
              </Avatar>
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleProfileMenuClose}
      >
        <MenuItem onClick={() => { navigate('/profile'); handleProfileMenuClose(); }}>
          <AccountCircle sx={{ mr: 1 }} /> Profile
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleLogout}>Logout</MenuItem>
      </Menu>

      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              bgcolor: 'background.paper',
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              bgcolor: 'background.paper',
              borderRight: '1px solid',
              borderColor: 'divider',
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          minHeight: '100vh',
          bgcolor: 'background.default',
        }}
      >
        <Toolbar />
        <Outlet />
      </Box>
    </Box>
  );
};
